/**
 * EPIC 56 — what every web view says about staffing, from the one shared
 * arithmetic (`staffingPlan.ts`). No view counts names any more:
 * `assignedWorkerIds` is the access / history list, and its length says
 * nothing about whether Thursday has somebody.
 *
 * Pure; the views turn these facts into words (`coverageLine`).
 */

import {
  jobCoverage,
  needOf,
  needToday,
  localDayStartMs,
  segmentsOf,
  shiftOccurrences,
  workerIdsOf,
  workerPlannedOn,
  workerWorkDays,
  type DayCoverage,
  type Segment,
  type ShiftOccurrence,
} from '@rendimees24/shared-types';
import type { Job } from '@rendimees24/shared-types';
import type { TFunction } from 'i18next';

type JobLike = Parameters<typeof jobCoverage>[0];

export interface CoverageSummary {
  /** Shifts judged (whole period, or from `from` on). */
  totalShifts: number;
  coveredShifts: number;
  shortShifts: number;
  overShifts: number;
  /** The first shift that is short, for "Neljapäeval puudub 1". */
  firstShort: DayCoverage | null;
  /** Places asked for at the same time. */
  required: number;
}

/**
 * Coverage of the job's shifts. `fromDate` (local YYYY-MM-DD) limits it to
 * that day and later — what staffing can still change.
 */
export function coverageSummary(job: JobLike, fromDate?: string): CoverageSummary {
  const need = needOf(job);
  const range = fromDate
    ? { startAt: new Date(localDayStartMs(need, fromDate)).toISOString() }
    : undefined;
  const cov = jobCoverage(job, range);
  return {
    totalShifts: cov.totalShifts,
    coveredShifts: cov.coveredShifts,
    shortShifts: cov.shortShifts,
    overShifts: cov.overShifts,
    firstShort: cov.days.find((d) => d.covered < d.required) ?? null,
    required: need.workerCount,
  };
}

/**
 * Is every shift covered? An order with no computable shift (legacy data with
 * no period) falls back to the people listed against the places — the only
 * fact there is.
 */
export function isFullyStaffed(job: JobLike, fromDate?: string): boolean {
  const s = coverageSummary(job, fromDate);
  if (s.totalShifts > 0) return s.shortShifts === 0;
  return workerIdsOf(segmentsOf(job)).length >= s.required;
}

/** Today's local date on the order's own clock. */
export function jobToday(job: JobLike, now: string | number | Date = Date.now()): string {
  return needToday(needOf(job), now);
}

/**
 * Places held for the WHOLE period: the fewest people on at any shift, capped
 * at the places. Jaan Mon–Tue + Peeter Wed–Fri on one place = 1.
 */
export function placesCovered(job: JobLike & { workEnded?: { endAt: string } | null }): number {
  // Work ended early: the shifts after the end were never meant to be filled.
  const cov = jobCoverage(job, job.workEnded?.endAt ? { endAt: job.workEnded.endAt } : undefined);
  const required = needOf(job).workerCount;
  if (cov.totalShifts === 0) return Math.min(required, workerIdsOf(segmentsOf(job)).length);
  return Math.min(required, ...cov.days.map((d) => d.covered));
}

/** One person's segments on the job, earliest first. */
export function workerSegments(job: JobLike, workerId: string): Segment[] {
  return segmentsOf(job)
    .filter((s) => s.workerId === workerId)
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/**
 * The last instant inside an exclusive end — what a DATE of the end means.
 * A segment ending at local midnight ends on the day before.
 */
export function lastInstant(endAt: string): string {
  const ms = Date.parse(endAt);
  return endAt.length > 10 && Number.isFinite(ms) ? new Date(ms - 1).toISOString() : endAt;
}

/** First and last of the person's own time on the job, or null. */
export function workerSpan(
  job: JobLike,
  workerId: string,
): { startAt: string; endAt: string } | null {
  const segs = workerSegments(job, workerId);
  if (segs.length === 0) return null;
  return {
    startAt: segs[0].startAt,
    endAt: segs.reduce((m, s) => (s.endAt > m ? s.endAt : m), segs[0].endAt),
  };
}

/** The person's planned work days on the job (shift-clipped), earliest first. */
export function workerDays(job: JobLike, workerId: string): ShiftOccurrence[] {
  return workerWorkDays(job, workerId);
}

/**
 * People whose own segments put them on today's shift (the order's local
 * day) — not everyone the order has ever listed.
 */
export function workersPlannedToday(
  job: JobLike,
  now: string | number | Date = Date.now(),
): string[] {
  const today = jobToday(job, now);
  return workerIdsOf(segmentsOf(job)).filter((w) => workerPlannedOn(job, w, today));
}

/**
 * A person's planned days as runs of consecutive shifts — "02.11–04.11,
 * 06.11". Consecutive means no working day of the order between them, so a
 * weekend does not break Mon–Fri + Mon. `fromDate` drops days before it.
 */
export function workerDayRanges(
  job: JobLike,
  workerId: string,
  fromDate?: string,
): Array<{ from: string; to: string }> {
  const all = shiftOccurrences(needOf(job));
  const index = new Map(all.map((o, i) => [o.date, i]));
  const days = [...new Set(workerWorkDays(job, workerId).map((d) => d.date))]
    .filter((d) => !fromDate || d >= fromDate)
    .sort();
  const out: Array<{ from: string; to: string; last: number }> = [];
  for (const d of days) {
    const i = index.get(d) ?? -2;
    const run = out[out.length - 1];
    if (run && i === run.last + 1) {
      run.to = d;
      run.last = i;
    } else out.push({ from: d, to: d, last: i });
  }
  return out.map(({ from, to }) => ({ from, to }));
}

/** "02.11–04.11, 06.11" */
export function formatDayRanges(ranges: Array<{ from: string; to: string }>): string {
  return ranges
    .map((r) => (r.from === r.to ? shortDate(r.from) : `${shortDate(r.from)}–${shortDate(r.to)}`))
    .join(', ');
}

/** People with a segment running now or ahead — who is still coming. */
export function peopleAhead(job: JobLike, now: number = Date.now()): number {
  const iso = new Date(now).toISOString();
  return workerIdsOf(segmentsOf(job).filter((s) => s.endAt > iso)).length;
}

/** People on a shift that overlaps `[dayStartIso, dayEndIso)`. */
export function workersOnDay(job: JobLike, dayStartIso: string, dayEndIso: string): string[] {
  return workerIdsOf(segmentsOf(job).filter((s) => s.startAt < dayEndIso && s.endAt > dayStartIso));
}

/** ISO weekday 1 (Mon) … 7 (Sun) of a local YYYY-MM-DD. */
export function weekdayOf(ymd: string): number {
  const dow = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  return dow === 0 ? 7 : dow;
}

/** "29.10" from a local YYYY-MM-DD. */
export function shortDate(ymd: string): string {
  return `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}`;
}

/**
 * The staffing state in words — "Neljapäeval (29.10) puudub 1", "Kaetud
 * vahetusi 4/5", "Kõik vahetused kaetud". Keys under `common:coverage.*`.
 */
export function coverageLine(t: TFunction, s: CoverageSummary): string {
  if (s.totalShifts === 0) return t('common:coverage.noShifts');
  if (s.firstShort) {
    const day = t(`common:coverage.on.${weekdayOf(s.firstShort.date)}`);
    const first = t('common:coverage.shortOn', {
      day,
      date: shortDate(s.firstShort.date),
      count: s.firstShort.shortBy,
    });
    return s.shortShifts > 1
      ? `${first} · ${t('common:coverage.shifts', { covered: s.coveredShifts, total: s.totalShifts })}`
      : first;
  }
  return t('common:coverage.allCovered', { total: s.totalShifts });
}

/** Short ratio for tight cells: "4/5 vahetust". */
export function coverageRatio(t: TFunction, s: CoverageSummary): string {
  if (s.totalShifts === 0) return t('common:coverage.noShifts');
  return t('common:coverage.shifts', { covered: s.coveredShifts, total: s.totalShifts });
}

/** Group sum over trades — the order's line in the list. */
export function sumCoverage(jobs: Job[], fromDate?: string): CoverageSummary {
  const parts = jobs.map((j) => coverageSummary(j, fromDate));
  const firstShort =
    parts
      .map((p) => p.firstShort)
      .filter((d): d is DayCoverage => !!d)
      .sort((a, b) => a.startMs - b.startMs)[0] ?? null;
  return {
    totalShifts: parts.reduce((n, p) => n + p.totalShifts, 0),
    coveredShifts: parts.reduce((n, p) => n + p.coveredShifts, 0),
    shortShifts: parts.reduce((n, p) => n + p.shortShifts, 0),
    overShifts: parts.reduce((n, p) => n + p.overShifts, 0),
    firstShort,
    required: parts.reduce((n, p) => n + p.required, 0),
  };
}
